import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Stack, Chip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import dayjs from "dayjs";
import { NormalButton } from "../../../components/ui/CommonReusables";
import RichEditor from "../../../components/rich-editor";
import { dialogCloseSx, primaryButtonSx, countChipSx } from "./buttonStyles";
import { isEmptyHtml } from "../utils/html";

const Field = ({ label, children }) => (
	<Box>
		<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
			{label}
		</Typography>
		<Box sx={{ mt: 0.5 }}>{children}</Box>
	</Box>
);

const Description = ({ html, emptyLabel }) =>
	isEmptyHtml(html) ? (
		<Typography variant="body2" color="text.secondary">
			{emptyLabel}
		</Typography>
	) : (
		// The editor renders its own saved markup, so the view matches the compose experience exactly.
		<RichEditor value={html} readOnly />
	);

const ViewCurriculumDialog = ({ open, onClose, curriculum }) => {
	if (!curriculum) return null;

	const trainings = curriculum.trainings ?? [];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Elementor sizes the paper to its content and ignores MUI's maxWidth, so the md
						// breakpoint is pinned here. Width stays margin-aware so it never overflows.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "900px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							{curriculum.title}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Created {dayjs(curriculum.created_at).format("MMM D, YYYY")} · {trainings.length}{" "}
							{trainings.length === 1 ? "training" : "trainings"}
						</Typography>
					</Box>

					<IconButton onClick={onClose} size="small" aria-label="Close curriculum details" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2.5}>
					<Field label="Description">
						<Description html={curriculum.description} emptyLabel="No description provided." />
					</Field>

					<Divider />

					<Field label={`Trainings (${trainings.length})`}>
						{trainings.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No trainings added yet.
							</Typography>
						) : (
							<Stack spacing={1.5} sx={{ mt: 1 }}>
								{trainings.map((training) => (
									<Box key={training.id} sx={{ border: "1px solid #D6E6F5", borderRadius: 2, p: 2, backgroundColor: "#FBFDFF" }}>
										<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
											<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
												<SchoolOutlinedIcon fontSize="small" sx={{ color: "#3A7DC9", flexShrink: 0 }} />
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={training.title}>
														{training.title}
													</Typography>
													<Typography variant="caption" color="text.secondary" noWrap>
														{dayjs(training.date).format("MMM D, YYYY")}
													</Typography>
												</Box>
											</Stack>

											<Chip
												size="small"
												icon={<GroupsIcon />}
												label={training.attendees.length}
												sx={{
													flexShrink: 0,
													...countChipSx,
													cursor: "default",
													"& .MuiChip-icon": { color: "#1A3F7A !important", fontSize: 15 },
												}}
											/>
										</Stack>

										<Description html={training.description} emptyLabel="No description provided." />
									</Box>
								))}
							</Stack>
						)}
					</Field>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<NormalButton onClick={onClose} sx={primaryButtonSx}>
					Close
				</NormalButton>
			</DialogActions>
		</Dialog>
	);
};

export default ViewCurriculumDialog;
